import angular from 'angular'

import setCss from '../utils/set-css'
import Emitter from '../utils/emitter'
import StrictObj from '../utils/strict-obj'
import extractRawElm from '../utils/extract-raw-elm'

import { toObject } from '../utils/bits'
import { once, isString, uniqueId } from 'lodash-es'
import { offEvent, onEventPassive } from '../utils/on-event'

angular
  .module('ggs.frame')
  .factory('Frame', FrameProvider)

const DEFAULT_ATTEMPT_LIMIT = 1

function FrameProvider(
  qh,
  $log,
  domh,
  FrameHack,
  FrameLoaded,
  DefineMethod,
  DefineHandler) {

  return function Frame(
    src,
    $parentElm,
    attemptLimit = DEFAULT_ATTEMPT_LIMIT,
    attemptDelay) {

    const parentElm = extractRawElm($parentElm)
    const iframeElm = document.createElement('iframe')

    setCss(iframeElm, { width: '100%', height: '100%' })
    iframeElm.setAttribute('allow', 'autoplay')
    iframeElm.setAttribute('tabindex', -1)
    iframeElm.setAttribute('frameborder', 0)
    iframeElm.setAttribute('allowfullscreen', true)
    iframeElm.setAttribute('allowtransparency', true)
    parentElm.appendChild(iframeElm)

    let frameHack = null
    let currentLoaded = null
    let currentAttempt = 0

    const sign = `frame[uid=${uniqueId()}]`
    const frame = StrictObj({
      sign,
      wrapM: DefineMethod(sign),
      wrapH: DefineHandler(sign),
      message: Emitter(sign)
    })

    frame.wrapM('load', once(() => {
      onEventPassive(window, 'message', frame.messageHandler)
      return frame.attempt()
    }))

    frame.wrapM('destroy', once(() => {
      domh.remove.notStrict(iframeElm)
      iframeElm.src = ''
      if (frameHack) frameHack.destroy()
      if (currentLoaded) currentLoaded.kill()
      offEvent(window, 'message', frame.messageHandler)
    }))

    frame.wrapM('send', qh.makeOver(frame.load, (msg) => {
      const str = isString(msg) ? msg : JSON.stringify(msg)
      if (iframeElm.contentWindow && iframeElm.contentWindow.postMessage) {
        iframeElm.contentWindow.postMessage(str, '*')
      }
    }))

    frame.wrapH('messageHandler', (event) => {
      if (event.source === iframeElm.contentWindow) {
        frame.message.emit(toObject(event.data))
      }
    })

    frame.wrapM('attempt', () => {
      currentAttempt++
      iframeElm.src = src
      currentLoaded = FrameLoaded(iframeElm, attemptDelay)
      return currentLoaded.then(
        frame.processLoadSuccess,
        frame.processLoadFailure)
    })

    frame.wrapM('processLoadSuccess', () => {
      frameHack = FrameHack(iframeElm)
      return iframeElm
    })

    frame.wrapM('processLoadFailure', (err) => {
      $log.warn(`Frame: cannot load ${src} at ${currentAttempt} of ${attemptLimit} attempts due to`, err)
      if (currentAttempt >= attemptLimit) {
        return frame.destroy().then(() => {
          throw new Error(`Frame: could not load ${src} in ${currentAttempt} attempts!`)
        })
      } else {
        return frame.attempt()
      }
    })

    return frame
  }
}
